import React, { useMemo, useState } from 'react';
import { WhatsAppTemplate } from '../../types';
import { Cadence, CadenceStepIn, cadenceApi } from '../../services/coreApi';
import WhatsAppBubble from '../WhatsAppTemplates/WhatsAppBubble';
import { byId, isSendable, isStaff, render } from '../WhatsAppTemplates/shape';
import { Modal, Pill, btn, btnTone, input, label } from '../../components/Kit';

/** The editor: the sequence on the left, the selected step as the lead sees
 *  it on the right. A step is `T + d days h hours`, or `day d at HH:MM` IST
 *  (the same clock time for every lead; core spreads a cohort over the
 *  following hour); the picker offers only
 *  self-complete templates (a bare {{n}} fails at Meta with 132000). Save is
 *  one PUT with a client-minted id — a step keeps its id so its receipts stay
 *  valid; a removed one is gone. No pause, no status. */

interface Step extends CadenceStepIn { key: number; }

// Display arithmetic for "lands": T + offset, moved to the next 08:00 IST when
// it falls outside the send window. The window is nurture's (SEND_WINDOW in
// its env); the real due moment stays its business — this only shows where
// a step falls. Wall-clock reads go through Intl; the one offset literal is
// for turning an IST wall-clock string back into an instant.
const IST = 'Asia/Kolkata';
const IST_OFFSET = '+05:30';
const WINDOW_IST: [number, number] = [8, 22];
const istHour = new Intl.DateTimeFormat('en-GB', { timeZone: IST, hour: 'numeric', hour12: false });
const istDay = new Intl.DateTimeFormat('en-CA', { timeZone: IST, dateStyle: 'short' }); // YYYY-MM-DD
const istTime = new Intl.DateTimeFormat('en-GB', { timeZone: IST, hour: '2-digit', minute: '2-digit', hour12: false });
const istLabel = new Intl.DateTimeFormat('en-GB', { timeZone: IST, weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false });

const landsAt = (anchorIso: string, d: number, h: number, at: string | null): { text: string; clamped: boolean } => {
  let t = new Date(new Date(anchorIso).getTime() + (d * 24 + h) * 60 * 60 * 1000);
  if (at) t = new Date(`${istDay.format(t)}T${at}:00${IST_OFFSET}`); // day d (IST) at the clock time
  const hour = Number(istHour.format(t));
  const clamped = hour >= WINDOW_IST[1] || hour < WINDOW_IST[0];
  if (clamped) {
    const day = istDay.format(hour >= WINDOW_IST[1] ? new Date(t.getTime() + 24 * 60 * 60 * 1000) : t);
    t = new Date(`${day}T${String(WINDOW_IST[0]).padStart(2, '0')}:00:00${IST_OFFSET}`);
  }
  return { text: istLabel.format(t).replace(',', ''), clamped };
};

// <input type="datetime-local"> holds an IST wall clock: now, and back to an instant.
const nowLocalIso = (): string => `${istDay.format(new Date())}T${istTime.format(new Date())}`;
const fromLocal = (v: string): string => new Date(`${v}:00${IST_OFFSET}`).toISOString();

const uuid = (): string =>
  (crypto as any).randomUUID
    ? (crypto as any).randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });

const CadenceEditor: React.FC<{
  cadence: Cadence | null;
  templates: WhatsAppTemplate[];
  onClose: () => void;
  onSaved: () => void;
}> = ({ cadence, templates, onClose, onSaved }) => {
  const [name, setName] = useState(cadence?.name || '');
  const [steps, setSteps] = useState<Step[]>(
    (cadence?.steps || []).map((s, i) => ({ key: i, id: s.id, offset_days: s.offset_days, offset_hours: s.offset_hours, at_time: s.at_time ? s.at_time.slice(0, 5) : null, template_id: s.template_id })),
  );
  const [nextKey, setNextKey] = useState(steps.length);
  const [selected, setSelected] = useState(0);
  const [anchor, setAnchor] = useState(nowLocalIso());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const all = useMemo(() => byId(templates), [templates]);
  // What a step may point at: lead-facing, live, and composable as its
  // approved shape — unfilled blanks fail at Meta with 132000, a mismatched
  // component shape (a media header with no media, a carousel card without
  // its picture) with 132012. `isSendable` is the sender's own rule.
  const pickable = useMemo(
    () => templates.filter((t) => !isStaff(t) && !t.retired_at && t.status === 'APPROVED'
                                  && isSendable(t, t.parent_id ? all[t.parent_id] : undefined))
      .sort((a, b) => a.display_name.localeCompare(b.display_name)),
    [templates, all],
  );

  const update = (key: number, patch: Partial<Step>) =>
    setSteps((prev) => prev.map((s) => (s.key === key ? { ...s, ...patch } : s)));
  const remove = (key: number) => {
    setSteps((prev) => prev.filter((s) => s.key !== key));
    setSelected(0);
  };
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= steps.length) return;
    setSteps((prev) => {
      const next = prev.slice();
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
    if (selected === i) setSelected(j);
    else if (selected === j) setSelected(i);
  };
  const add = () => {
    const last = steps[steps.length - 1];
    setSteps((prev) => [...prev, { key: nextKey, offset_days: last ? last.offset_days + 1 : 0, offset_hours: 0, at_time: last?.at_time ?? null, template_id: pickable[0]?.id || '' }]);
    setSelected(steps.length);
    setNextKey(nextKey + 1);
  };

  const save = async () => {
    setError(null);
    if (!name.trim()) { setError('Name the cadence.'); return; }
    if (steps.some((s) => !s.template_id)) { setError('Every step needs a template.'); return; }
    setSaving(true);
    try {
      await cadenceApi.put(cadence?.id || uuid(), {
        name: name.trim(),
        steps: steps.map(({ id, offset_days, offset_hours, at_time, template_id }) => ({ id, offset_days, offset_hours, at_time, template_id })),
      });
      onSaved();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err.message);
      setSaving(false);
    }
  };

  const destroy = async () => {
    if (!cadence) return;
    setError(null);
    setSaving(true);
    try {
      await cadenceApi.remove(cadence.id);
      onSaved();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err.message);
      setSaving(false);
    }
  };

  const sel = steps[selected];
  const selTemplate = sel ? all[sel.template_id] : undefined;
  const rendered = selTemplate ? render(selTemplate, selTemplate.parent_id ? all[selTemplate.parent_id] : undefined) : null;
  const anchorIso = fromLocal(anchor);
  const untouched = !!cadence && cadence.enrolled === 0 && cadence.completed === 0;

  const small = `${input} h-8 w-[52px] px-2 text-right`;
  const icon = 'w-7 h-7 grid place-items-center rounded-md text-graphite hover:text-charcoal hover:bg-charcoal/5 disabled:opacity-30 disabled:hover:bg-transparent';

  return (
    <Modal
      wide
      title={cadence ? cadence.name : 'New cadence'}
      onClose={onClose}
      footer={
        <>
          {error && <span className="text-brick text-[12.5px] mr-auto">{error}</span>}
          {!error && cadence && <span className="mr-auto text-graphite text-xs">saved {new Date(cadence.updated_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}</span>}
          {untouched && <button type="button" onClick={destroy} disabled={saving} className={`${btn} ${btnTone.brick}`}>Delete</button>}
          <button type="button" onClick={onClose} className={`${btn} ${btnTone.charcoal}`}>Cancel</button>
          <button type="button" onClick={save} disabled={saving} className={`${btn} ${btnTone.forest}`}>{saving ? 'Saving…' : 'Save'}</button>
        </>
      }
    >
      <div className="flex items-end gap-4 flex-wrap mb-6">
        <div className="flex-1 min-w-[260px]">
          <label className={label}>Name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name this cadence" className={`${input} w-full`} />
        </div>
        <Pill tone={cadence?.enrolled ? 'ok' : undefined}>
          {cadence ? `${cadence.enrolled} enrolled · ${cadence.completed} completed` : 'nobody attached yet'}
        </Pill>
      </div>

      <div className="grid gap-8 items-start" style={{ gridTemplateColumns: 'minmax(0,3fr) minmax(0,2fr)' }}>
        <div>
          <div className="flex items-end justify-between gap-3 pb-2.5 flex-wrap">
            <h3 className="font-display text-[16px] font-semibold tracking-tight">Steps</h3>
            <span className="text-graphite text-xs flex items-center gap-1.5">
              lands for a lead attached
              <input type="datetime-local" value={anchor} onChange={(e) => e.target.value && setAnchor(e.target.value)} className={`${input} h-7 px-2 text-[12px]`} />
            </span>
          </div>
          <ol className="border-t border-charcoal/15">
            {steps.length === 0 && <li className="text-graphite text-[13px] text-center py-8">No steps yet.</li>}
            {steps.map((s, i) => {
              const L = landsAt(anchorIso, s.offset_days, s.offset_hours, s.at_time);
              const t = all[s.template_id];
              const on = i === selected;
              return (
                <li key={s.key} onClick={() => setSelected(i)}
                    className={`grid items-center gap-3 px-2 py-2.5 border-b border-charcoal/[0.06] cursor-pointer transition-colors ${on ? 'bg-sage/20 shadow-[inset_3px_0_0_theme(colors.forest)]' : 'hover:bg-charcoal/[0.025]'}`}
                    style={{ gridTemplateColumns: '24px minmax(0,1fr) auto 112px auto' }}>
                  <span className={`w-6 h-6 grid place-items-center rounded-full text-[11.5px] font-semibold num ${on ? 'bg-forest text-warm-white' : 'bg-charcoal/[0.06] text-graphite'}`}>{i + 1}</span>
                  <select value={s.template_id} onClick={(e) => e.stopPropagation()} onChange={(e) => update(s.key, { template_id: e.target.value })}
                          className={`${input} h-8 w-full min-w-0 ${t ? '' : 'border-brick'}`}>
                    {!t && <option value={s.template_id}>{s.template_id ? 'not pickable any more · choose again' : 'choose a template'}</option>}
                    {pickable.map((p) => <option key={p.id} value={p.id}>{p.display_name}{p.parent_id ? ` · fills ${all[p.parent_id]?.name || ''}` : ''}</option>)}
                  </select>
                  <span className="flex items-center gap-1 whitespace-nowrap text-[12.5px] text-graphite" onClick={(e) => e.stopPropagation()}>
                    <span className="font-semibold text-forest mr-0.5">T +</span>
                    <input type="number" min={0} value={s.offset_days}
                           onChange={(e) => update(s.key, { offset_days: Math.max(0, Number(e.target.value) || 0) })} className={small} />d
                    {s.at_time ? (
                      <>
                        <span className="ml-1">at</span>
                        <input type="time" value={s.at_time} step={300}
                               onChange={(e) => update(s.key, { at_time: e.target.value || '09:30', offset_hours: 0 })} className={`${small} w-[88px]`} />
                        <button type="button" onClick={() => update(s.key, { at_time: null })} title="Hours after T instead" className={icon}>h</button>
                      </>
                    ) : (
                      <>
                        <input type="number" min={0} max={23} value={s.offset_hours}
                               onChange={(e) => update(s.key, { offset_hours: Math.max(0, Number(e.target.value) || 0) })} className={`${small} ml-1`} />h
                        <button type="button" onClick={() => update(s.key, { at_time: '09:30', offset_hours: 0 })} title="A clock time instead (same for every lead)" className={icon}>at</button>
                      </>
                    )}
                  </span>
                  <span className="text-right whitespace-nowrap num text-[12.5px] text-charcoal">
                    {L.text}{L.clamped && <span className="text-amber-ink font-semibold" title="Outside the send window — moved to its next opening"> ↦</span>}
                  </span>
                  <span className="flex items-center" onClick={(e) => e.stopPropagation()}>
                    <button type="button" onClick={() => move(i, -1)} disabled={i === 0} title="Move up" className={icon}>↑</button>
                    <button type="button" onClick={() => move(i, 1)} disabled={i === steps.length - 1} title="Move down" className={icon}>↓</button>
                    <button type="button" onClick={() => remove(s.key)} title="Remove step" className={`${icon} hover:text-brick`}>✕</button>
                  </span>
                </li>
              );
            })}
          </ol>
          <div className="flex items-center gap-3 pt-3">
            <button type="button" onClick={add} className={`${btn} ${btnTone.charcoal}`}>+ Add step</button>
            <span className="text-[12px] text-graphite">{pickable.length} pickable templates</span>
          </div>
        </div>

        <div>
          <div className="pb-2.5"><h3 className="font-display text-[16px] font-semibold tracking-tight">The lead sees</h3></div>
          <div className="border-t border-charcoal/15 pt-4">
            {rendered && sel ? (
              <>
                <WhatsAppBubble r={rendered} wide />
                <div className="text-[12px] text-graphite mt-2">
                  Step {selected + 1} · {selTemplate!.name} · lands {landsAt(anchorIso, sel.offset_days, sel.offset_hours, sel.at_time).text}{sel.at_time && ' (+ up to an hour, spread per lead)'}
                </div>
              </>
            ) : <div className="text-graphite text-[13px] py-4">Pick a step to see the message.</div>}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CadenceEditor;
